<div class="modal-header" id="address-picker-modal-title">
    <h3 class="modal-title" translate="TIMESHEET_ADDRESS_LABEL"></h3>
    <button
        class="btn-close"
        type="button"
        (click)="modalInstance.hide()"
        [attr.aria-label]="'TIMESHEET_CANCEL_LABEL' | translate"
    ></button>
</div>

<div class="modal-body">
    <div class="address-picker__toolbar">
        <input
            type="search"
            class="form-control form-control-sm address-picker__search"
            [value]="searchTerm()"
            (input)="onSearchInput($event)"
            [placeholder]="'TIMESHEET_ADDRESS_SEARCH_PLACEHOLDER' | translate"
            [attr.aria-label]="'TIMESHEET_ADDRESS_SEARCH_LABEL' | translate"
        />
    </div>

    <table class="table table-sm align-middle mb-0 address-picker__table">
        <thead>
            <tr>
                <th scope="col" translate="TIMESHEET_ADDRESS_LIST_NAME"></th>
                <th scope="col" translate="TIMESHEET_ADDRESS_LIST_CITY"></th>
                <th scope="col"></th>
            </tr>
        </thead>
        <tbody>
            @for (address of filteredAddresses(); track address.id) {
                <tr [attr.data-entry-id]="address.id">
                    <td>
                        <div>
                            {{ address.addrText || address.name1 }}
                        </div>
                        @if (address.street) {
                            <small class="text-muted">{{ address.street }}</small>
                        }
                    </td>
                    <td>
                        {{ cityWithUsState(address) }}
                        @if (address.postCode) {
                            <small class="text-muted d-block">{{ address.postCode }}</small>
                        }
                    </td>
                    <td class="address-picker__actions">
                        <!--
                            One split button per row rather than a row of buttons: selecting an
                            address is the row's whole point and stays a single tap, while
                            duplicating, editing and hiding/deleting are occasional actions worth a
                            click through the dropdown instead of permanent visual weight on every
                            row.
                        -->
                        <!--
                            `container="body"` because this modal is `modal-dialog-scrollable`: the
                            body scrolls, so it clips anything positioned inside it, and the menu of
                            the last row would be cut off at the footer. Rendering it at body level
                            takes it out of that scroll container — a z-index alone cannot lift an
                            element out of an ancestor that clips it (see `bs-dropdown-container` in
                            styles.scss for the z-index the move then needs).

                            Positioning then belongs to ngx-bootstrap rather than to bootstrap's own
                            CSS, so the alignment has to be stated: `bottom end` hangs the menu under
                            the split button's right edge, where the button itself sits. Its default,
                            `bottom start`, aligns the left edges and leaves a menu wider than the
                            button reaching past the screen, which the positioning service then
                            answers by shifting the whole menu left of the button.
                        -->
                        <div class="btn-group" dropdown container="body" placement="bottom end">
                            <button
                                class="btn btn-primary"
                                type="button"
                                (click)="select(address)"
                                translate="TIMESHEET_SELECT_ADDRESS_LABEL"
                            ></button>
                            <button
                                type="button"
                                class="btn btn-primary dropdown-toggle dropdown-toggle-split"
                                dropdownToggle
                                [attr.aria-label]="'TIMESHEET_ADDRESS_ACTIONS_MENU_ARIA_LABEL' | translate"
                            >
                                <span class="visually-hidden">Toggle Dropdown</span>
                            </button>
                            <ul class="dropdown-menu" *dropdownMenu role="menu">
                                <li role="menuitem">
                                    <button
                                        class="dropdown-item"
                                        type="button"
                                        (click)="startDuplicatingAddress(address)"
                                    >
                                        <fa-icon [icon]="faCopy"></fa-icon>
                                        {{ 'TIMESHEET_ADDRESS_DUPLICATE_ADDRESS_BUTTON_LABEL' | translate }}
                                    </button>
                                </li>
                                @if (isEditable(address)) {
                                    <li role="menuitem">
                                        <button
                                            class="dropdown-item"
                                            type="button"
                                            (click)="startEditingAddress(address)"
                                        >
                                            <fa-icon [icon]="faPencil"></fa-icon>
                                            {{ 'TIMESHEET_ADDRESS_EDIT_ADDRESS_BUTTON_LABEL' | translate }}
                                        </button>
                                    </li>
                                }
                                @if (enableHiding()) {
                                    <!--
                                        Hiding is offered only where it is safe: a backend-supplied
                                        address — a customer address or an AddressSet row alike — the
                                        timesheet is not currently pointing at. A self-added one is
                                        deleted instead, since it has no backend copy to fall back to
                                        and hiding it would leave it unreachable.
                                    -->
                                    @if (address.allowToHide) {
                                        <li role="menuitem">
                                            <button class="dropdown-item" type="button" (click)="hide(address)">
                                                <fa-icon [icon]="faEyeSlash"></fa-icon>
                                                {{ 'TIMESHEET_ADDRESS_HIDE_ADDRESS_BUTTON_LABEL' | translate }}
                                            </button>
                                        </li>
                                    } @else if (canDelete(address)) {
                                        <li role="menuitem">
                                            <button class="dropdown-item" type="button" (click)="delete(address)">
                                                <fa-icon [icon]="faTrashAlt"></fa-icon>
                                                {{ 'TIMESHEET_ADDRESS_DELETE_ADDRESS_BUTTON_LABEL' | translate }}
                                            </button>
                                        </li>
                                    }
                                }
                            </ul>
                        </div>
                    </td>
                </tr>
            } @empty {
                <tr>
                    <!--
                        Two different reasons produce an empty table, and they need different
                        messages: an address book with nothing in it at all is the original error
                        message, but a book that has addresses which the search has all excluded
                        needs to say that instead, or the user is told to add data they already have.
                    -->
                    <td
                        colspan="3"
                        [translate]="
                            addresses().length === 0
                                ? 'TIMESHEET_ADDRESS_FORM_ERROR_LABEL'
                                : 'TIMESHEET_ADDRESS_NO_MATCHES_LABEL'
                        "
                    ></td>
                </tr>
            }
        </tbody>
    </table>

    @if (formMode()) {
        <div class="address-picker__form" #addressForm>
            <h4
                class="h6"
                [translate]="isEditingAddress() ? 'TIMESHEET_ADDRESS_EDIT_TITLE' : 'TIMESHEET_ADDRESS_TITLE'"
            ></h4>
            <!--
                Field order follows the legacy form: the free-text line first, then the structured
                address. The three fields an address cannot be sent without carry an asterisk; free
                text is a note beside them, not a way of writing the address instead.
            -->
            <div class="row g-2">
                <div class="col-12">
                    <label class="form-label" translate="TIMESHEET_ADDRESS_TEXT"></label>
                    <pace-text-field
                        [formField]="newAddressForm.addrText"
                        [ariaLabel]="'TIMESHEET_ADDRESS_TEXT' | translate"
                    ></pace-text-field>
                </div>
                <div class="col-8">
                    <label class="form-label">
                        {{ 'TIMESHEET_ADDRESS_STREET_TEXT' | translate }}
                        <span aria-hidden="true">*</span>
                    </label>
                    <pace-text-field
                        [formField]="newAddressForm.street"
                        [ariaLabel]="'TIMESHEET_ADDRESS_STREET_TEXT' | translate"
                    ></pace-text-field>
                </div>
                <div class="col-4">
                    <label class="form-label">
                        {{ 'TIMESHEET_ADDRESS_POSTCODE_TEXT' | translate }}
                        <span aria-hidden="true">*</span>
                    </label>
                    <pace-text-field
                        [formField]="newAddressForm.postCode"
                        [ariaLabel]="'TIMESHEET_ADDRESS_POSTCODE_TEXT' | translate"
                    ></pace-text-field>
                </div>
                <div class="col-8">
                    <label class="form-label">
                        {{ 'TIMESHEET_ADDRESS_CITY_TEXT' | translate }}
                        <span aria-hidden="true">*</span>
                    </label>
                    <pace-text-field
                        [formField]="newAddressForm.city"
                        [ariaLabel]="'TIMESHEET_ADDRESS_CITY_TEXT' | translate"
                    ></pace-text-field>
                </div>
                <div class="col-4">
                    <!--
                        Shown, not asked for: a timesheet address always carries the selected user's
                        country, whatever the form holds.
                    -->
                    <label class="form-label" translate="TIMESHEET_ADDRESS_COUNTRY_TEXT"></label>
                    <!--
                        A plain read-only input rather than a bound field: the draft keeps the code the
                        backend expects, while the user reads the country's name.
                    -->
                    <input
                        class="form-control input-md text-field"
                        type="text"
                        readonly
                        [value]="countryDisplayName()"
                        [attr.aria-label]="'TIMESHEET_ADDRESS_COUNTRY_TEXT' | translate"
                    />
                </div>
                @if (enableStatesOfUSA()) {
                    <div class="col-4">
                        <label class="form-label" translate="TIMESHEET_ADDRESS_STATE_LABEL"></label>
                        <pace-select-field
                            [formField]="newAddressForm.stateOfUSA"
                            [options]="statesOfUSAOptions()"
                            [ariaLabel]="'TIMESHEET_ADDRESS_STATE_LABEL' | translate"
                        ></pace-select-field>
                    </div>
                }
            </div>

            <div class="d-flex gap-2 justify-content-end mt-3">
                <button
                    class="btn btn-secondary"
                    type="button"
                    (click)="closeForm()"
                    translate="TIMESHEET_CANCEL_LABEL"
                ></button>
                <button
                    class="btn btn-primary"
                    type="button"
                    [disabled]="!newAddressForm().valid()"
                    (click)="saveAddress()"
                    [translate]="isEditingAddress() ? 'TIMESHEET_SAVE_LABEL' : 'TIMESHEET_ADD_LABEL'"
                ></button>
            </div>
        </div>
    }
</div>

<div class="modal-footer">
    @if (enableHiding()) {
        <button
            class="btn me-auto btn-warning"
            type="button"
            [disabled]="!hasHiddenAddresses()"
            (click)="resetVisibility()"
            translate="TIMESHEET_RESET_ADDRESS_LABEL"
        ></button>
    }
    <button
        class="btn btn-primary"
        type="button"
        [disabled]="!!formMode()"
        (click)="startAddingAddress()"
        translate="TIMESHEET_NEW_LABEL"
    ></button>
    <button
        class="btn btn-secondary"
        type="button"
        (click)="modalInstance.hide()"
        translate="TIMESHEET_CANCEL_LABEL"
    ></button>
</div>
